/**
 * What this viewer may do with a saved view, in the vocabulary the affordances
 * use — written down ONCE, here, because every surface that offers saved views
 * asks the same four questions and `saved-views-api` answers them with four
 * different scopes.
 *
 * The mapping, from the route tags and `SavedViewService._guard_scope`:
 *
 * | Affordance                    | Scope                      |
 * |-------------------------------|----------------------------|
 * | Save / rename / delete my own | `saved-views:personalize`  |
 * | Save one others can see       | `saved-views:create`       |
 * | Mint + manage share links     | `saved-views:edit`         |
 * | Delete one others can see     | `saved-views:delete`       |
 *
 * The split exists so every tier can keep a view of its own while "and now
 * everyone in my tenant sees it" stays gated. Ownership (can I touch *this*
 * row?) is ABAC and stays the API's to enforce — the UI cannot know it, and a
 * button that 403s on somebody else's row is a smaller problem than one that is
 * missing on your own.
 */
import * as React from "react";
import type { SharePrincipalOption } from "@authe/saved-views";
import { usePermissions } from "@authe/ui-core/permissions";
import { RESOURCES } from "@/config";
import { useMyTeamsRead } from "./useMyTeams";

export interface SavedViewPermissions {
  /** Keep, rename and delete views of your own. */
  canPersonalize: boolean;
  /** Publish a view beyond yourself (the "Shared" scope in the save dialog). */
  canPublish: boolean;
  /** Mint, revoke and edit tokenized share links. */
  canShare: boolean;
  /** Delete a view that is published beyond `user` scope. */
  canDeleteShared: boolean;
}

/**
 * The affordance → scope mapping, as data so it can be checked.
 *
 * A `can()` miss is a DENIAL, not an error: name a verb the catalogue does not
 * carry and the button silently disappears for everyone, Admin included. Naming
 * them here (rather than inline in the hook) is what lets a test assert they are
 * real catalogue verbs without rendering anything.
 */
export const SAVED_VIEW_SCOPES = {
  canPersonalize: "personalize",
  canPublish: "create",
  canShare: "edit",
  canDeleteShared: "delete",
} as const satisfies Record<keyof SavedViewPermissions, string>;

export function useSavedViewPermissions(): SavedViewPermissions {
  const { can } = usePermissions();
  return {
    canPersonalize: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canPersonalize),
    canPublish: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canPublish),
    canShare: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canShare),
    canDeleteShared: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canDeleteShared),
  };
}

/** The audience picker's options, and whether the list can be trusted. */
export interface ShareTeamOptions {
  options: SharePrincipalOption[];
  /**
   * The memberships read failed. `options` is then whatever was already in hand
   * — possibly nothing — and NOT an answer about who this caller works with.
   */
  failed: boolean;
  /** Re-read the memberships. */
  refetch: () => void;
}

/**
 * The caller's teams, in the shape the share dialog's audience picker takes.
 *
 * One hook rather than the same three-line rename beside every `ShareViewDialog`
 * — a `team`-target link is gated on membership of the ids it carries, so
 * getting them from anywhere but the caller's real memberships mints a link
 * nobody can open.
 *
 * `useMyTeamsRead`, not `useMyTeams`: an empty list has two causes and the bare
 * list cannot tell them apart. A picker with no chips then reads as "you are on
 * no teams" after a failed read, and the owner either mints a link naming nobody
 * or concludes they cannot share with a team that exists. `failed` travels with
 * the options so the surface can say which it is and offer the retry — the same
 * shape `components/TeamShareField` already renders.
 */
export function useShareTeamOptions(): ShareTeamOptions {
  const { teams, failed, refetch } = useMyTeamsRead();
  const options = React.useMemo(
    () => teams.map((team) => ({ id: team.id, label: team.name })),
    [teams],
  );
  return { options, failed, refetch };
}

/**
 * Can a LIVE (dynamic) share actually be served to whoever opens it?
 *
 * Yes, for INTERNAL links, which is the only audience the share dialog offers
 * `dynamic` to. The private (Auth0) viewer mounts the same seed resolver this
 * page does — `createTransactionGraphSeedResolver` from `@authe/tx-semantic` —
 * so a live link replays there exactly as it does here. The one thing it cannot
 * do is read a COLD body straight off the CDN (it holds no CloudFront
 * signed-cookie session), so it falls through to the authenticated
 * `GET /transactions/{id}/body` route instead; same answer, one more hop.
 *
 * A PUBLIC link is still static-only, and not because of this flag: there is no
 * anonymous data source to re-fetch from, so `ShareViewDialog` pins `static`
 * whenever the audience is external.
 *
 * Kept as a named constant rather than inlined `true` because it is the switch
 * to flip if a viewer ever loses its resolver again — one place, not one per
 * surface.
 */
export const LIVE_SHARES_SERVABLE = true;
